/**
 * Field Manual — how to play, written as a staff handbook on paper. `FieldManualContent` is also
 * shown from the in-game menu. The prose lives in the catalogue (`fm.*`, rich text).
 */
import { FACTION_COLORS } from '@friction/engine';
import { useRef, useState } from 'react';
import { type MsgKey, type Translator, useT } from '../../i18n';
import { startAcademy } from '../../tutorial/launch';
import { cx } from '../common/controls';
import { Paper } from '../common/Panel';
import { richText } from '../common/RichText';
import { UnitCounter } from '../common/UnitCounter';
import { hotkeys } from '../game/hotkeys';
import m from './manual.module.css';
import { ScreenFrame } from './ScreenFrame';

const CHAPTERS = ['command', 'fog', 'combat', 'supply', 'weather', 'economy', 'victory', 'keys'] as const;
const NUM = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII'];

const RED = FACTION_COLORS[0];
const BLUE = FACTION_COLORS[1];

/** Terrain table: move cost and defence multiplier (Game-Design.md), notes from the catalogue. */
const TERRAIN: {
  id: 3 | 4 | 5 | 6 | 7 | 8;
  note: 'plains' | 'forest' | 'hills' | 'mountains' | 'marsh' | 'urban';
  move: number;
  def: number;
}[] = [
  { id: 3, note: 'plains', move: 1, def: 1 },
  { id: 4, note: 'forest', move: 1.6, def: 1.35 },
  { id: 5, note: 'hills', move: 1.7, def: 1.5 },
  { id: 6, note: 'mountains', move: 3, def: 2.2 },
  { id: 7, note: 'marsh', move: 2.5, def: 1.25 },
  { id: 8, note: 'urban', move: 1.2, def: 1.8 },
];

function CommandFigure({ t }: { t: Translator }) {
  return (
    <svg width="330" height="130" viewBox="0 0 330 130" aria-hidden>
      <circle cx="62" cy="65" r="54" fill="rgba(178,58,46,0.08)" stroke="#8c7a5a" strokeDasharray="4 3" />
      <text
        x="62"
        y="124"
        textAnchor="middle"
        fontSize="10"
        fill="#5a5043"
        fontFamily="IBM Plex Sans Condensed"
      >
        {t('fm.command.fig.phone')}
      </text>
      <foreignObject x="40" y="44" width="46" height="42">
        <UnitCounter type="hq" color={RED} size={44} />
      </foreignObject>
      <path
        d="M100 70 C 150 30, 200 110, 262 66"
        fill="none"
        stroke="#7a5c3a"
        strokeWidth="2"
        strokeDasharray="6 4"
      />
      <g transform="translate(176 62)">
        <rect x="-9" y="-7" width="18" height="13" rx="1.5" fill="#efe6cf" stroke="#2b2620" />
        <path d="M-8 -6l8 6 8-6" fill="none" stroke="#2b2620" />
      </g>
      <text
        x="176"
        y="100"
        textAnchor="middle"
        fontSize="10"
        fill="#5a5043"
        fontFamily="IBM Plex Sans Condensed"
      >
        {t('fm.command.fig.courier')}
      </text>
      <foreignObject x="262" y="44" width="50" height="44">
        <UnitCounter type="infantry" color={RED} size={46} />
      </foreignObject>
    </svg>
  );
}

function FlankFigure({ t }: { t: Translator }) {
  const defender = t('fm.combat.fig.defender');
  const hex = (cx: number, cy: number, r: number) =>
    Array.from({ length: 6 }, (_, i) => {
      const a = (Math.PI / 180) * (60 * i - 30);
      return `${cx + r * Math.cos(a)},${cy + r * Math.sin(a)}`;
    }).join(' ');
  const arrow = (a: number) => {
    const r0 = 62;
    const r1 = 34;
    const x0 = 80 + r0 * Math.cos(a);
    const y0 = 65 + r0 * Math.sin(a);
    const x1 = 80 + r1 * Math.cos(a);
    const y1 = 65 + r1 * Math.sin(a);
    return (
      <line x1={x0} y1={y0} x2={x1} y2={y1} stroke="#b23a2e" strokeWidth="5" markerEnd="url(#fm-head)" />
    );
  };
  return (
    <svg width="160" height="130" viewBox="0 0 160 130" aria-hidden>
      <defs>
        <marker
          id="fm-head"
          viewBox="0 0 10 10"
          refX="5"
          refY="5"
          markerWidth="3"
          markerHeight="3"
          orient="auto"
        >
          <path d="M0 0L10 5L0 10z" fill="#b23a2e" />
        </marker>
      </defs>
      <polygon points={hex(80, 65, 28)} fill="rgba(47,93,138,0.18)" stroke="#2b2620" strokeWidth="1.4" />
      <text
        x="80"
        y="70"
        textAnchor="middle"
        // "Verteidiger" is longer than "defender": smaller, so it stays inside the hex.
        fontSize={defender.length > 9 ? 10 : 12}
        fontFamily="EB Garamond"
        fontStyle="italic"
        fill="#2b2620"
      >
        {defender}
      </text>
      {arrow(Math.PI)}
      {arrow(-Math.PI / 3)}
      {arrow(Math.PI / 3)}
    </svg>
  );
}

/** `onAcademy`: offer the interactive Kriegsakademie (menu only — in a match it would end the war). */
export function FieldManualContent({ onAcademy }: { onAcademy?: () => void } = {}) {
  const t = useT();
  const pageRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState<string>(CHAPTERS[0]);
  const rich = (key: MsgKey) => richText(t.plain(key), { term: m.term, key: m.keyCap });
  const list = (keys: MsgKey[]) => (
    <ul>
      {keys.map((k) => (
        <li key={k}>{rich(k)}</li>
      ))}
    </ul>
  );

  const jump = (id: string) => {
    setActive(id);
    pageRef.current?.querySelector(`#fm-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const onScroll = () => {
    const page = pageRef.current;
    if (!page) return;
    const top = page.getBoundingClientRect().top;
    let current: string = CHAPTERS[0];
    for (const c of CHAPTERS) {
      const el = page.querySelector(`#fm-${c}`);
      if (el && el.getBoundingClientRect().top - top < 120) current = c;
    }
    if (current !== active) setActive(current);
  };
  const heading = (i: number, key: MsgKey) => (
    <h3>
      <span>{NUM[i]}.</span> {t.plain(key)}
    </h3>
  );

  return (
    <div className={m.wrap}>
      <nav className={m.toc} aria-label={t('fm.chapters')}>
        {CHAPTERS.map((c, i) => (
          <button
            key={c}
            type="button"
            className={cx(m.tocItem, active === c && m.tocOn)}
            onClick={() => jump(c)}
          >
            <span className={m.tocNum}>{NUM[i]}.</span>
            {t(`fm.ch.${c}`)}
          </button>
        ))}
        {onAcademy && (
          <button type="button" className={m.academy} onClick={onAcademy}>
            <span className={m.academyKicker}>{t('fm.academy.kicker')}</span>
            <span className={m.academyTitle}>{t('fm.academy.title')}</span>
            <span className={m.academyText}>{t('fm.academy.text')}</span>
          </button>
        )}
      </nav>
      <Paper className={m.page}>
        <div ref={pageRef} onScroll={onScroll} className={m.scroll}>
          <div className={m.docHead}>
            <span>{t('fm.docHead')}</span>
            <span>{t('fm.docClass')}</span>
          </div>

          <section id="fm-command" className={m.chapter}>
            {heading(0, 'fm.command.h')}
            <p>{rich('fm.command.p')}</p>
            <div className={m.figure}>
              <CommandFigure t={t} />
              <div className={m.caption}>{rich('fm.command.caption')}</div>
            </div>
            {list(['fm.command.li1', 'fm.command.li2', 'fm.command.li3', 'fm.command.li4'])}
          </section>

          <section id="fm-fog" className={m.chapter}>
            {heading(1, 'fm.fog.h')}
            <p>{rich('fm.fog.p')}</p>
            <div className={m.figure}>
              <div className={m.levels}>
                <div className={m.level}>
                  <UnitCounter type={null} color={BLUE} size={56} />
                  {t('fm.fog.contact')}
                </div>
                <div className={m.level}>
                  <UnitCounter type="armor" color={BLUE} size={56} />
                  {t('fm.fog.identified')}
                </div>
                <div className={m.level}>
                  <UnitCounter type="armor" color={BLUE} size={56} />
                  <span className={m.est}>
                    <i style={{ width: '70%' }} />
                    <i style={{ width: '45%', background: '#c9a13f' }} />
                  </span>
                  {t('fm.fog.assessed')}
                </div>
                <div className={m.level}>
                  <UnitCounter type="armor" color={BLUE} size={56} exposed />
                  {t('fm.fog.unmasked')}
                </div>
              </div>
              <div className={m.caption}>{rich('fm.fog.caption')}</div>
            </div>
            {list(['fm.fog.li1', 'fm.fog.li2', 'fm.fog.li3'])}
          </section>

          <section id="fm-combat" className={m.chapter}>
            {heading(2, 'fm.combat.h')}
            <div className={m.figure}>
              <FlankFigure t={t} />
              <div className={m.caption}>{rich('fm.combat.caption')}</div>
            </div>
            <p>{rich('fm.combat.p')}</p>
            <table className={m.tableT}>
              <thead>
                <tr>
                  <th>{t('fm.combat.col.terrain')}</th>
                  <th>{t('fm.combat.col.move')}</th>
                  <th>{t('fm.combat.col.defence')}</th>
                  <th>{t('fm.combat.col.notes')}</th>
                </tr>
              </thead>
              <tbody>
                {TERRAIN.map((row) => (
                  <tr key={row.id}>
                    <td>{t(`terrain.${row.id}`)}</td>
                    <td>{t.num(row.move, 1)}</td>
                    <td>×{t.num(row.def, row.def === Math.round(row.def * 10) / 10 ? 1 : 2)}</td>
                    <td>{t(`fm.combat.note.${row.note}`)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p>{t('fm.combat.night')}</p>
          </section>

          <section id="fm-supply" className={m.chapter}>
            {heading(3, 'fm.supply.h')}
            <p>{rich('fm.supply.p1')}</p>
            <p>{rich('fm.supply.p2')}</p>
          </section>

          <section id="fm-weather" className={m.chapter}>
            {heading(4, 'fm.weather.h')}
            <p>{rich('fm.weather.p1')}</p>
            {list(['fm.weather.li1', 'fm.weather.li2', 'fm.weather.li3', 'fm.weather.li4'])}
            <p>{rich('fm.weather.p2')}</p>
          </section>

          <section id="fm-economy" className={m.chapter}>
            {heading(5, 'fm.economy.h')}
            {list(['fm.economy.li1', 'fm.economy.li2', 'fm.economy.li3'])}
          </section>

          <section id="fm-victory" className={m.chapter}>
            {heading(6, 'fm.victory.h')}
            {list(['fm.victory.li1', 'fm.victory.li2', 'fm.victory.li3', 'fm.victory.li4'])}
          </section>

          <section id="fm-keys" className={m.chapter}>
            {heading(7, 'fm.keys.h')}
            <div className={m.keys}>
              {hotkeys(t).map((k) => (
                <div key={k.label} className={m.keyRow}>
                  <span>{k.label}</span>
                  <span className={m.keyCaps}>
                    {k.keys.map((c) =>
                      c === '–' ? (
                        <span key={c}>–</span>
                      ) : (
                        <kbd key={c} className={m.keyCap}>
                          {c}
                        </kbd>
                      ),
                    )}
                  </span>
                </div>
              ))}
            </div>
          </section>
        </div>
      </Paper>
    </div>
  );
}

export function FieldManualScreen() {
  const t = useT();
  return (
    <ScreenFrame kicker={t('fm.kicker')} title={t('fm.title')} width={1180}>
      <FieldManualContent onAcademy={() => startAcademy()} />
    </ScreenFrame>
  );
}
